import { z } from 'zod';
const short = (max) => z.string().trim().max(max);
const yesNo = z.enum(['yes', 'no']);
/** A house rule the broker states: yes, no, or "ask the broker". */
export const RULE_ANSWERS = ['yes', 'no', 'ask'];
const rule = z.enum(RULE_ANSWERS);
const count = (max) => z.number().int().min(0).max(max);
export const FURNISHING_ITEMS = [
    'wardrobes',
    'ac',
    'bed',
    'geyser',
    'washing_machine',
    'fridge',
    'sofa',
    'tv',
    'modular_kitchen',
];
export const STAY_RULES = ['family', 'bachelors', 'couples', 'sharing', 'non_veg', 'pets'];
export const PARKING_TYPES = ['none', 'two_wheeler', 'car', 'both'];
export const BROKERAGE_PAYERS = ['tenant', 'owner', 'split'];
export const OWNER_SIGNING = ['himself', 'no', 'power_of_attorney'];
/**
 * What the broker tells about the flat beyond the basics. Every field is optional: a sparse
 * listing is allowed, and a blank term is shown as "Ask the broker", never a made up default.
 * These are things people said; the app does not check them.
 */
export const propertyFactsSchema = z
    .object({
    brokerage: z
        .object({
        kind: z.enum(['amount', 'months']),
        amountPaise: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable().optional(),
        months: z.number().min(0).max(12).nullable().optional(),
        payer: z.enum(BROKERAGE_PAYERS),
    })
        .nullable()
        .optional(),
    furnishingItems: z.array(z.enum(FURNISHING_ITEMS)).max(FURNISHING_ITEMS.length).optional(),
    stay: z.object(Object.fromEntries(STAY_RULES.map((k) => [k, rule.optional()]))).optional(),
    terms: z
        .object({
        lockInMonths: count(60).nullable().optional(),
        noticeDays: count(365).nullable().optional(),
        rentIncreasePct: z.number().min(0).max(100).nullable().optional(),
        depositRefundDays: count(365).nullable().optional(),
    })
        .optional(),
    building: z
        .object({
        totalFloors: count(200).nullable().optional(),
        lift: yesNo.nullable().optional(),
        parkingType: z.enum(PARKING_TYPES).nullable().optional(),
        parkingCount: count(20).nullable().optional(),
        waterSupply: short(80).nullable().optional(),
        powerBackup: short(80).nullable().optional(),
        gated: yesNo.nullable().optional(),
        ageYears: count(200).nullable().optional(),
        facing: short(30).nullable().optional(),
        societyRules: short(300).nullable().optional(),
        visitorTiming: short(120).nullable().optional(),
        nearestMainRoad: short(120).nullable().optional(),
    })
        .optional(),
    location: z
        .object({
        latitude: z.number().min(-90).max(90).nullable().optional(),
        longitude: z.number().min(-180).max(180).nullable().optional(),
        note: short(120).nullable().optional(),
    })
        .optional(),
    owner: z
        .object({
        livesInSociety: yesNo.nullable().optional(),
        signing: z.enum(OWNER_SIGNING).nullable().optional(),
    })
        .optional(),
    photos: z
        .object({
        representative: z.boolean().optional(),
        takenOn: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
    })
        .optional(),
})
    .strict();
/** A Google Maps link (or "lat,lng" text) to a point. Null when no coordinates can be read. */
export function parseMapPoint(text) {
    const trimmed = text.trim();
    const patterns = [
        /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,
        /[?&](?:q|query|destination|ll)=(-?\d{1,2}\.\d+)(?:,|%2C)(-?\d{1,3}\.\d+)/,
        /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,
        /^(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/,
    ];
    for (const pattern of patterns) {
        const found = pattern.exec(trimmed);
        if (found === null)
            continue;
        const latitude = Number(found[1]);
        const longitude = Number(found[2]);
        if (Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180)
            return { latitude, longitude };
    }
    return null;
}
/** A plain directions link to a point. No routing service and no commute time. */
export function directionsLink(latitude, longitude) {
    return `https://www.google.com/maps/dir/?api=1&destination=${String(latitude)},${String(longitude)}`;
}
export function mapLink(latitude, longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${String(latitude)},${String(longitude)}`;
}
export const COMPLETENESS_TOTAL = 14;
/** Which of the 14 details are filled, for the meter in the form. A listing with few is still allowed. */
export function listingCompleteness(input) {
    const f = input.facts;
    const checks = [
        ['brokerage', f.brokerage != null],
        ['furnishing', input.furnishing !== null],
        ['stay', Object.values(f.stay ?? {}).some((v) => v !== undefined)],
        ['lockIn', f.terms?.lockInMonths != null],
        ['notice', f.terms?.noticeDays != null],
        ['increase', f.terms?.rentIncreasePct != null],
        ['refund', f.terms?.depositRefundDays != null],
        ['availableFrom', input.availableFrom !== null],
        ['floor', input.floor !== null && f.building?.totalFloors != null],
        ['lift', f.building?.lift != null],
        ['parking', f.building?.parkingType != null],
        ['water', (f.building?.waterSupply ?? '') !== ''],
        ['powerBackup', (f.building?.powerBackup ?? '') !== ''],
        ['location', f.location?.latitude != null && f.location.longitude != null],
    ];
    const missing = checks.filter(([, ok]) => !ok).map(([name]) => name);
    return { filled: checks.length - missing.length, total: COMPLETENESS_TOTAL, missing };
}
/** A listing can be shared once the brokerage is stated: the tenant should never have to ask first. */
export function readyToShare(facts) {
    const b = facts.brokerage;
    if (b == null)
        return false;
    return b.kind === 'amount' ? b.amountPaise != null : b.months != null;
}
//# sourceMappingURL=property-facts.js.map